import {flatten, partition} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {CreateNewGroupDropdownContainer} from '../../../../../../../../../../src/components/common/dropdowns/cardGroups/createNewGroupDropdownContainer';
import {ModifyCardGroupsDropdownTypesEnum} from '../../../../../../../../../../src/components/common/dropdowns/cardGroups/modifyCardGroupDropdown';
import {ModifyCardGroupsDropdownContainer} from '../../../../../../../../../../src/components/common/dropdowns/cardGroups/modifyCardGroupDropdownContainer';
import {CardGroupModel} from '../../../../../../../../../../src/models/api/cardGroupModel';
import {Trans} from '../../../../../../../../../shared/helpers/src/internationalization/react';
import {
  AsyncHandlerOf,
  Handler,
  HandlerOf,
} from '../../../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {DropdownFooter} from '../../../../../../../../../shared/ui/src/components/dropdowns/dropdownFooter';
import {PopoverTransferProps} from '../../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';

/*
 * Props.
 */

interface ContactsTableMultiListsCardsDropdownProps {
  popoverProps: PopoverTransferProps;
  namespace: string;
  type: ModifyCardGroupsDropdownTypesEnum;
  alreadySelectedGroupIds?: ReadonlyArray<number>;
  allCardsGroupIds?: ReadonlyArray<number>;

  onAddGroupsClick: AsyncHandlerOf<ReadonlyArray<CardGroupModel>>;
  onRemoveGroupsClick: AsyncHandlerOf<ReadonlyArray<CardGroupModel>>;
  onGroupCreated?: HandlerOf<CardGroupModel>;
}

/*
 * Style.
 */

const StyledDropdownFooterContentDiv = styled.div`
  display: flex;
  flex-flow: row-reverse;
  gap: 8px;
`;

/*
 * Component.
 */

export const ContactsTableMultiListsCardsDropdown: FC<ContactsTableMultiListsCardsDropdownProps> = (
  props,
) => {
  const {
    alreadySelectedGroupIds = [],
    allCardsGroupIds = [],
    popoverProps,
    namespace,
    type,
    onAddGroupsClick,
    onRemoveGroupsClick,
    onGroupCreated,
  } = props;

  const [isShowingCreateGroupDropdown, setIsShowingCreateGroupDropdown] = useState(false);

  const onNewGroupClick = () => {
    setIsShowingCreateGroupDropdown(true);
  };

  if (isShowingCreateGroupDropdown) {
    return (
      <CreateNewGroupDropdownContainer
        popoverProps={popoverProps}
        namespace={namespace}
        onClose={() => setIsShowingCreateGroupDropdown(false)}
        onGroupCreated={(group) => {
          onAddGroupsClick([group]);
          popoverProps.onRequestClose();
          if (onGroupCreated) {
            onGroupCreated(group);
          }
        }}
      />
    );
  }

  return (
    <ModifyCardGroupsDropdownContainer
      popoverProps={popoverProps}
      namespace={namespace}
      type={type}
      onSortGroups={(groups) =>
        flatten(partition(groups, (group) => alreadySelectedGroupIds.includes(group.id)))
      }
      // Only allow creating a new group when adding.
      onNewGroupClick={type === ModifyCardGroupsDropdownTypesEnum.ADD ? onNewGroupClick : undefined}
      // We want to disable groups that already are added to all cards when adding new groups.
      disabledGroupIds={type === ModifyCardGroupsDropdownTypesEnum.ADD ? alreadySelectedGroupIds : undefined}
      // These are groups that should already be selected
      alreadySelectedGroupIds={
        type === ModifyCardGroupsDropdownTypesEnum.ADD ? alreadySelectedGroupIds : undefined
      }
      // Only pass in available group ids when we are removing.
      availableGroupIds={type === ModifyCardGroupsDropdownTypesEnum.REMOVE ? allCardsGroupIds : undefined}
      renderFooter={(selectedGroups) => (
        <DropdownFooter>
          <StyledDropdownFooterContentDiv>
            {renderDropdownFooterPrimaryActionButton(
              popoverProps,
              type,
              selectedGroups.length === 0,
              () => {
                // Pass all the selected group ids but remove any that are already selected.
                onAddGroupsClick(
                  selectedGroups.filter((group) => !alreadySelectedGroupIds.includes(group.id)),
                );
              },
              () => {
                onRemoveGroupsClick(selectedGroups);
              },
            )}
            <Button variant={ButtonVariantsEnum.SECONDARY} onClick={() => popoverProps.onRequestClose()}>
              <Trans id="contact-manager-table-multi-select-actions-dropdown-footer-cancel">Cancel</Trans>
            </Button>
          </StyledDropdownFooterContentDiv>
        </DropdownFooter>
      )}
    />
  );
};

/*
 * Helpers.
 */

function renderDropdownFooterPrimaryActionButton(
  popoverProps: PopoverTransferProps,
  type: ModifyCardGroupsDropdownTypesEnum,
  isDisabled: boolean,
  onAddSelectedGroupsClick: Handler,
  onRemoveGroupsClick: Handler,
) {
  if (type === ModifyCardGroupsDropdownTypesEnum.REMOVE) {
    return (
      <Button
        variant={ButtonVariantsEnum.DANGER}
        isDisabled={isDisabled}
        onClick={(event) => {
          event.preventDefault();
          popoverProps.onRequestClose();
          onRemoveGroupsClick();
        }}
      >
        <Trans id="contact-manager-table-multi-select-actions-dropdown-footer-remove">Remove</Trans>
      </Button>
    );
  }
  return (
    <Button
      variant={ButtonVariantsEnum.PRIMARY}
      isDisabled={isDisabled}
      onClick={(event) => {
        event.preventDefault();
        popoverProps.onRequestClose();
        onAddSelectedGroupsClick();
      }}
    >
      <Trans id="contact-manager-table-multi-select-actions-dropdown-footer-add">Add</Trans>
    </Button>
  );
}
